import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCreateMaintenanceEvent, useMaintenanceSchedules, MAINTENANCE_TASK_TYPES } from '../../hooks/useMaintenance'
import { usePrinters } from '../../hooks/usePrinters'
import { BackLink } from '../../components/BackLink'

export default function MaintenanceLogForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const scheduleIdParam = searchParams.get('schedule')
  const { data: schedules } = useMaintenanceSchedules()
  const { data: printers } = usePrinters()
  const createEvent = useCreateMaintenanceEvent()

  const matchedSchedule = schedules?.find((s) => s.id === scheduleIdParam)
  const today = new Date().toISOString().slice(0, 10)

  const [printerId, setPrinterId] = useState(matchedSchedule?.printer_id ?? printers?.[0]?.id ?? '')
  const [taskType, setTaskType] = useState<string>(matchedSchedule?.task_type ?? 'nozzle-swap')
  const [eventDate, setEventDate] = useState(today)
  const [printerHours, setPrinterHours] = useState(() => {
    const p = printers?.find((p) => p.id === printerId)
    return p ? String(p.total_print_hours) : ''
  })
  const [cost, setCost] = useState('')
  const [notes, setNotes] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    try {
      await createEvent.mutateAsync({
        printer_id: printerId,
        schedule_id: matchedSchedule?.id ?? null,
        event_date: eventDate,
        task_type: taskType,
        printer_hours_at_event: printerHours ? Number(printerHours) : null,
        cost: cost ? Number(cost) : null,
        notes: notes || null,
      })
      navigate('/maintenance')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/maintenance" />
      <h1>Log maintenance</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field
          label="Printer"
          value={printerId}
          onChange={setPrinterId}
          selectOptions={(printers ?? []).map((p) => ({ value: p.id, label: p.name }))}
          required
        />
        <Field
          label="Task"
          value={taskType}
          onChange={setTaskType}
          select={MAINTENANCE_TASK_TYPES as unknown as string[]}
        />
        <Field label="Date" value={eventDate} onChange={setEventDate} type="date" required />
        <Field label="Printer hours at event" value={printerHours} onChange={setPrinterHours} type="decimal" hint="Auto-filled from your printer's total hours" />
        <Field label="Cost (optional)" value={cost} onChange={setCost} type="decimal" hint="Parts cost — also logs an expense entry" />
        <Field label="Notes (optional)" value={notes} onChange={setNotes} multiline />

        <button
          type="submit"
          disabled={createEvent.isPending}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {createEvent.isPending ? 'Saving…' : 'Log maintenance'}
        </button>
        {errorMessage && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-3)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', hint, required, multiline, select, selectOptions }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  multiline?: boolean
  select?: string[]
  selectOptions?: { value: string; label: string }[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {selectOptions ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {selectOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} style={commonStyle} />
      ) : (
        <input type={type === 'date' ? 'date' : 'text'} inputMode={type === 'decimal' ? 'decimal' : undefined} required={required} value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle} />
      )}
      {hint && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>{hint}</p>}
    </div>
  )
}
